/* IPRTablePicker — Structured IPR entries (iter 96c).
 *
 * Replaces the free-text "IPR by region & stage" input on the Planner's
 * Author Plan form. Each row captures:
 *   - tooth_pair    : human label like "31-32" (FDI inter-proximal contact)
 *   - amount_mm     : float — clinical max 0.5mm per contact, hard cap 1.0mm
 *   - stage         : aligner stage (1-indexed)
 *   - note          : optional free text
 *
 * When entries exist, the row's amount_mm > 0.5mm flips the row to a warn
 * tone (clinical guidance is 0.5mm max per contact). Total IPR is shown at
 * the bottom + auto-derived from sum of entries (the parent will likely
 * also push this into `total_ipr_mm` for backward compat).
 *
 * The component is fully controlled — `value` is the array, `onChange`
 * returns the new array.
 */
import React, { useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Plus, X as XIcon } from "@phosphor-icons/react";
import CIBanner from "@/components/case/CIBanner";


const MAX_PER_CONTACT = 0.5;
const HARD_CAP = 1.0;


export default function IPRTablePicker({ value = [], onChange, totalAligners = 0, readOnly = false }) {
    const rows = Array.isArray(value) ? value : [];

    const total = useMemo(
        () => rows.reduce((acc, r) => acc + (Number(r.amount_mm) || 0), 0),
        [rows]
    );
    const anyOverCap = rows.some((r) => Number(r.amount_mm || 0) > MAX_PER_CONTACT);

    const set = (next) => onChange && onChange(next);

    const addRow = () => {
        if (readOnly) return;
        set([...rows, { tooth_pair: "", amount_mm: 0.3, stage: 1, note: "" }]);
    };

    const updateRow = (idx, patch) => {
        if (readOnly) return;
        const next = rows.map((r, i) => (i === idx ? { ...r, ...patch } : r));
        set(next);
    };

    const removeRow = (idx) => {
        if (readOnly) return;
        set(rows.filter((_, i) => i !== idx));
    };

    return (
        <div data-testid="ipr-table-picker" className="space-y-2">
            <div className="flex items-center justify-between gap-2">
                <p className="text-[11px] text-muted-foreground">
                    Structured per-contact reduction. Clinical max {MAX_PER_CONTACT}mm per contact.
                </p>
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addRow}
                    disabled={readOnly}
                    className="h-8 text-xs"
                    data-testid="ipr-add-row"
                >
                    <Plus size={12} weight="bold" className="mr-1" /> Add IPR
                </Button>
            </div>

            {rows.length === 0 ? (
                <p className="text-[11px] text-muted-foreground italic py-2">
                    No IPR planned yet. Click "Add IPR" to add a contact.
                </p>
            ) : (
                <div className="overflow-x-auto rounded-lg border border-border">
                    <table className="w-full text-sm">
                        <thead className="bg-secondary/40">
                            <tr>
                                <th className="text-left px-2 py-1.5 text-[10px] font-bold uppercase tracking-wide text-muted-foreground" style={{ width: 120 }}>Tooth pair</th>
                                <th className="text-left px-2 py-1.5 text-[10px] font-bold uppercase tracking-wide text-muted-foreground" style={{ width: 110 }}>Amount (mm)</th>
                                <th className="text-left px-2 py-1.5 text-[10px] font-bold uppercase tracking-wide text-muted-foreground" style={{ width: 80 }}>Stage</th>
                                <th className="text-left px-2 py-1.5 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">Note</th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r, idx) => {
                                const amt = Number(r.amount_mm || 0);
                                const overCap = amt > MAX_PER_CONTACT;
                                return (
                                    <tr key={idx} className={"border-t border-border " + (overCap ? "bg-amber-50/50" : "")} data-testid={`ipr-row-${idx}`}>
                                        <td className="px-2 py-1.5">
                                            <Input
                                                value={r.tooth_pair || ""}
                                                onChange={(e) => updateRow(idx, { tooth_pair: e.target.value.toUpperCase() })}
                                                placeholder="e.g. 31-32"
                                                disabled={readOnly}
                                                className="h-8 font-mono text-xs"
                                                data-testid={`ipr-row-${idx}-tooth-pair`}
                                            />
                                        </td>
                                        <td className="px-2 py-1.5">
                                            <Input
                                                type="number"
                                                min="0.05"
                                                max={HARD_CAP}
                                                step="0.05"
                                                value={r.amount_mm ?? ""}
                                                onChange={(e) => updateRow(idx, { amount_mm: Number(e.target.value) || 0 })}
                                                disabled={readOnly}
                                                className={"h-8 font-mono text-xs " + (overCap ? "ring-1 ring-amber-300" : "")}
                                                data-testid={`ipr-row-${idx}-amount`}
                                            />
                                        </td>
                                        <td className="px-2 py-1.5">
                                            <Input
                                                type="number"
                                                min="1"
                                                max={totalAligners || 99}
                                                step="1"
                                                value={r.stage ?? ""}
                                                onChange={(e) => updateRow(idx, { stage: Math.max(1, Number(e.target.value) || 1) })}
                                                disabled={readOnly}
                                                className="h-8 font-mono text-xs"
                                                data-testid={`ipr-row-${idx}-stage`}
                                            />
                                        </td>
                                        <td className="px-2 py-1.5">
                                            <Input
                                                value={r.note || ""}
                                                onChange={(e) => updateRow(idx, { note: e.target.value })}
                                                placeholder="Optional"
                                                disabled={readOnly}
                                                className="h-8 text-xs"
                                                data-testid={`ipr-row-${idx}-note`}
                                            />
                                        </td>
                                        <td className="px-2 py-1.5 text-right">
                                            {!readOnly && (
                                                <button
                                                    type="button"
                                                    onClick={() => removeRow(idx)}
                                                    className="text-rose-600 hover:bg-rose-50 p-1 rounded transition-colors"
                                                    data-testid={`ipr-row-${idx}-remove`}
                                                    title="Remove row"
                                                >
                                                    <XIcon size={12} weight="bold" />
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                        <tfoot>
                            <tr className="border-t-2 border-border bg-secondary/30">
                                <td className="px-2 py-1.5 text-[10px] font-bold uppercase tracking-wide text-muted-foreground" colSpan={1}>Total</td>
                                <td className="px-2 py-1.5 font-display font-bold tabular-nums" data-testid="ipr-total">
                                    {total.toFixed(2)} mm
                                </td>
                                <td colSpan={3} />
                            </tr>
                        </tfoot>
                    </table>
                </div>
            )}

            <CIBanner
                kind="warn"
                show={anyOverCap}
                message={`One or more contacts exceed ${MAX_PER_CONTACT}mm`}
                secondary={`Clinical guidance: never reduce more than ${MAX_PER_CONTACT}mm at a single inter-proximal contact in one visit.`}
                testId="ci-ipr-per-contact"
            />
        </div>
    );
}
